/**
 * React hook for user media like state
 * 
 * Provides reactive access to like state from SecureStorage.
 * This hook automatically listens to storage changes and updates the component.
 * 
 * Benefits:
 * - Maintains reactivity (listens to 'secure-storage-change' events)
 * - Abstracts storage key format and storage implementation
 * - Automatically updates when like state changes (from any component)
 * - Easy to test and mock
 * 
 * Why a hook and not just a use case?
 * - Use cases provide one-time reads: await getUserMediaLikeUseCase.execute()
 * - Hooks provide reactive reads: const { isLiked } = useUserMediaLike()
 * - React's useEffect + event listeners = automatic UI updates
 * 
 * Usage:
 * ```tsx
 * const { isLiked, loading } = useUserMediaLike(media.relativePath);
 * ```
 */

import { useListenToSecureStorageUpdates } from '@/data/storage/local/secureStorageWebCrypto';
import type { UserMediaLikeStorage } from '@/domain/entities/secureStorage/secureStorageEntities';
import { SECURE_STORAGE_KEYS } from '@/domain/entities/secureStorage/secureStorageKeys';

export function useUserMediaLike(mediaRelativePath: string) {
    // Get storage key (abstracts the key format)
    const storageKey = SECURE_STORAGE_KEYS.LIKES + mediaRelativePath;

    // Use the reactive hook for automatic updates
    // This sets up an event listener for 'secure-storage-change' events
    const { data, loading } = useListenToSecureStorageUpdates<UserMediaLikeStorage>(storageKey);

    return {
        /** Full like data from storage */
        likeData: data,
        /** Whether data is still loading */
        loading,
    };
}
